import { BarChart3Icon, FileSpreadsheetIcon, GaugeIcon, SmartphoneIcon } from 'lucide-react'
import type { ProductPageContent } from '../../types/product'
import { industryCatalog, productCatalog } from './shared'

export const reports: ProductPageContent = {
  slug: 'reports',
  name: 'Reports & Insights',
  icon: BarChart3Icon,
  hero: {
    headline: 'Understand your business without waiting for month end',
    subheadline:
      'See sales, profit, and cash flow update as you trade, compare branches and products, and make decisions with real numbers.',
    trustLine: 'Live numbers from every sale and expense · Used by 7,300+ businesses',
    visual: {
      kind: 'devices',
      tablet: {
        module: 'Reports',
        greeting: 'Performance · Last 30 days',
        kpis: [
          { label: 'Revenue', value: 'RWF 12.8M', delta: { label: '+7.2%', tone: 'positive' } },
          { label: 'Gross profit', value: 'RWF 4.1M', delta: { label: '32% margin', tone: 'brand' } },
          { label: 'Cash balance', value: 'RWF 4.1M' },
        ],
        main: {
          type: 'bars',
          title: 'Weekly revenue',
          value: 'RWF 3.4M',
          delta: { label: '+12%', tone: 'positive' },
          bars: [
            { label: 'W1', value: 2.6 },
            { label: 'W2', value: 2.9 },
            { label: 'W3', value: 3.0 },
            { label: 'W4', value: 3.4, highlight: true },
          ],
        },
        side: {
          type: 'progress',
          title: 'Best sellers',
          rows: [
            { label: 'Bread loaf', value: '286', pct: 100 },
            { label: 'Milk 500ml', value: '221', pct: 77 },
            { label: 'Water 500ml', value: '156', pct: 55 },
          ],
        },
      },
      phone: {
        type: 'list',
        title: 'Today so far',
        badge: { label: 'Live', tone: 'positive' },
        rows: [
          { label: 'Sales', value: 'RWF 486,000' },
          { label: 'Expenses', value: 'RWF 72,500', tone: 'negative' },
          { label: 'Transactions', value: '94' },
        ],
        footer: { label: 'Net', value: 'RWF 413,500' },
      },
    },
  },
  stats: [
    { value: '7,300+', label: 'businesses checking their numbers with Kayko' },
    { value: '40+', label: 'ready-made reports, from daily sales to profit' },
    { value: 'Daily', label: 'how often most owners check performance' },
  ],
  benefits: {
    heading: 'Numbers you can act on.',
    items: [
      { icon: GaugeIcon, title: 'Always up to date', copy: 'Reports update with every sale, purchase, and expense — no manual entry.' },
      { icon: SmartphoneIcon, title: 'Check from anywhere', copy: 'See today’s sales and cash from your phone, even when you’re away from the shop.' },
      { icon: FileSpreadsheetIcon, title: 'Ready to share', copy: 'Export clean reports for your accountant, partners, or lender in one click.' },
    ],
  },
  features: {
    heading: 'Everything you need to understand performance.',
    subheading: 'Clear reports for owners, managers, and accountants.',
    items: [
      {
        title: 'Sales and profit at a glance',
        copy: 'See revenue, cost of goods, and profit for any day, week, or month. Understand which products actually make you money.',
        bullets: ['Profit by product and category', 'Margin after discounts and returns', 'Compare any two periods'],
        visual: {
          kind: 'card',
          card: {
            type: 'list',
            title: 'Profit & loss · September',
            rows: [
              { label: 'Revenue', value: 'RWF 12,845,000' },
              { label: 'Cost of goods', value: '−RWF 8,712,000', tone: 'negative' },
              { label: 'Gross profit', value: 'RWF 4,133,000', tone: 'positive' },
              { label: 'Operating expenses', value: '−RWF 1,921,000', tone: 'negative' },
            ],
            footer: { label: 'Net profit', value: 'RWF 2,212,000' },
          },
        },
      },
      {
        title: 'Cash flow you can see coming',
        copy: 'Track money in and money out across cash, mobile money, and bank. See what’s due from customers and to suppliers before it hits your account.',
        bullets: ['Balances by payment account', 'Upcoming receivables and payables', 'Weekly cash forecast'],
        visual: {
          kind: 'card',
          card: {
            type: 'bars',
            title: 'Cash balance',
            value: 'RWF 4,128,000',
            delta: { label: '+5.6% this month', tone: 'positive' },
            bars: [
              { label: 'Apr', value: 2.9 },
              { label: 'May', value: 3.1 },
              { label: 'Jun', value: 3.4 },
              { label: 'Jul', value: 3.3 },
              { label: 'Aug', value: 3.9 },
              { label: 'Sep', value: 4.1, highlight: true },
            ],
            legend: 'Mobile money RWF 2.3M · Bank RWF 1.5M · Cash RWF 0.3M',
          },
        },
      },
      {
        title: 'Compare branches, staff, and products',
        copy: 'See which location, team member, and product performs best. Put your time and stock where they earn the most.',
        bullets: ['Branch-by-branch comparisons', 'Top and slow-moving products', 'Busiest days and hours'],
        visual: {
          kind: 'card',
          card: {
            type: 'progress',
            title: 'Revenue by branch',
            meta: 'September',
            rows: [
              { label: 'Remera', value: 'RWF 5.6M', pct: 100 },
              { label: 'Kimironko', value: 'RWF 4.2M', pct: 75 },
              { label: 'Nyamirambo', value: 'RWF 3.0M', pct: 54 },
            ],
          },
        },
      },
      {
        title: 'Reports for your accountant and RRA',
        copy: 'Download VAT summaries, sales ledgers, and expense reports in the format your accountant needs. Filing becomes a morning’s work, not a week’s.',
        bullets: ['VAT and sales summaries by period', 'Export to spreadsheet or PDF', 'Share access with your accountant'],
        visual: {
          kind: 'card',
          card: {
            type: 'list',
            title: 'Exports',
            meta: 'Q3 2026',
            rows: [
              { label: 'VAT summary', sublabel: 'Jul – Sep', value: 'PDF', tone: 'brand' },
              { label: 'Sales ledger', sublabel: '4,812 entries', value: 'XLSX', tone: 'brand' },
              { label: 'Expense report', sublabel: '612 entries', value: 'XLSX', tone: 'brand' },
            ],
            action: 'Share with accountant',
          },
        },
      },
    ],
  },
  steps: {
    heading: 'Insights from your first day.',
    items: [
      { title: 'Record as you trade', copy: 'Sales, purchases, and expenses in Kayko feed your reports automatically.' },
      { title: 'Open your dashboard', copy: 'See today’s performance on your phone, tablet, or computer.' },
      { title: 'Decide with confidence', copy: 'Compare periods, branches, and products, then act on what you see.' },
    ],
  },
  related: {
    heading: 'Works with the rest of Kayko.',
    subheading: 'Reports bring together everything happening in your business.',
    items: [productCatalog['sales-pos'], productCatalog.expenses, productCatalog.inventory, productCatalog['business-finance']],
  },
  industries: {
    heading: 'Built for your industry.',
    subheading: 'Reports that make sense for the way you work.',
    items: [industryCatalog.retail, industryCatalog.food, industryCatalog.hotels, industryCatalog.manufacturing],
  },
  testimonial: {
    quote:
      'I used to wait for my accountant to tell me how the month went. Now I check profit every evening and I spotted a slow branch months earlier than I would have.',
    name: 'Claudine Uwase',
    role: 'Owner',
    business: 'Uwase Hardware',
    location: 'Gikondo, Kigali',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/ecd9d923-5c1a-43ac-a833-0b82a75cd9c0.jpg',
    metric: { value: '+18%', label: 'profit growth after acting on branch reports' },
  },
  pricingTeaser: { text: 'Reports & Insights is included in every Kayko plan.', linkLabel: 'See pricing', href: '#' },
  faq: {
    heading: 'Frequently asked questions',
    items: [
      { question: 'Where do the numbers come from?', answer: 'From the sales, purchases, expenses, and payments you record in Kayko. There is nothing extra to enter.' },
      { question: 'Can my accountant access reports?', answer: 'Yes. Invite your accountant with read-only access, or send exports by email.' },
      { question: 'Can I see reports on my phone?', answer: 'Yes. The dashboard and every report work on phones, tablets, and computers.' },
      { question: 'Do reports include VAT?', answer: 'Yes. VAT is calculated from your EBM receipts and purchases, and summarised by period.' },
      { question: 'Can I compare branches?', answer: 'Yes. Filter any report by branch, or compare locations side by side.' },
      { question: 'Can I include past data?', answer: 'Yes. Import historical sales and expenses from spreadsheets so your trends start from day one.' },
    ],
  },
  cta: {
    headline: 'See your business clearly.',
    subheadline: 'Your dashboard is ready as soon as you start recording, with support from our team in Kigali.',
  },
}
